import { combine, Val } from "value-enhancer";
import { IServiceVideoChatAvatar, IServiceVideoChatUID } from "@obsbot/remo-services";
import { SideEffectManager } from "side-effect-manager";
import { TencentRTCWeb } from "./tencent-rtc-web";

export enum TRTCStreamType {
    Main = "main",
    Sub = "sub",
}

export interface RTCRemoteAvatarConfig {
    rtc: TencentRTCWeb;
    uid: IServiceVideoChatUID;
    element?: HTMLElement | null;
}

export class RTCRemoteAvatar implements IServiceVideoChatAvatar {
    private readonly uid: IServiceVideoChatUID;
    private readonly _rtc: TencentRTCWeb;
    private readonly _sideEffect = new SideEffectManager();

    private readonly _shouldCamera$ = new Val(false);
    private readonly _shouldMic$ = new Val(false);
    private readonly _active$ = new Val(false);

    private readonly _el$: Val<HTMLElement | undefined | null>;

    public constructor(config: RTCRemoteAvatarConfig) {
        this._rtc = config.rtc;
        this.uid = config.uid;
        this._el$ = new Val(config.element);
        this._sideEffect.addDisposer(
            // 当三个条件同时满足时（有DOM元素、流已激活、麦克风开启）才会播放远程音频
            combine([this._el$, this._active$, this._shouldMic$]).subscribe(
                ([el, active, shouldMic]) => {
                    try {
                        // 静音掉某一个用户的声音，同时不再拉取该远端用户的音频数据流
                        this._rtc.rtc.muteRemoteAudio(this.uid, !(el && active && shouldMic));
                    } catch (e) {
                        console.error(e);
                    }
                },
            ),
        );
        this._sideEffect.addDisposer(
            // 当三个条件同时满足时（有DOM元素、流已激活、开启）才会播放远程视频
            combine([this._el$, this._active$, this._shouldCamera$]).subscribe(
                async ([el, active, shouldCamera]) => {
                    if (el && active) {
                        try {
                            if (shouldCamera) {
                                await this._rtc.rtc.startRemoteVideo({
                                    view: el,
                                    userId: this.uid,
                                    // @ts-ignore
                                    streamType: TRTCStreamType.Main,
                                    option: {
                                        mirror: false,
                                        fillMode: "cover",
                                        receiveWhenViewVisible: true,
                                    },
                                });
                            } else {
                                this._rtc.rtc.stopRemoteVideo({
                                    userId: this.uid,
                                    // @ts-ignore
                                    streamType: TRTCStreamType.Main,
                                });
                            }
                        } catch (e) {
                            console.error(e);
                            // 已经接收到remoteVideoAvailable, 重新进入频道时提示还没有发布流, 所以重试一下
                            // @ts-ignore
                            if (e.message.includes("does not publishing stream")) {
                                setTimeout(async () => {
                                    await this._rtc.rtc.startRemoteVideo({
                                        view: el,
                                        userId: this.uid,
                                        // @ts-ignore
                                        streamType: TRTCStreamType.Main,
                                        option: {
                                            mirror: false,
                                            fillMode: "cover",
                                            receiveWhenViewVisible: true,
                                        },
                                    });
                                }, 0);
                            }
                        }
                    }
                },
            ),
        );
    }
    public setCameraId(_cameraId: string): void {
        throw new Error("Remote Avatar does not support setCameraId");
    }
    public setMicId(_micId: string): void {
        throw new Error("Remote Avatar does not support setMicId");
    }
    public setIsFrontCamera(_isFrontCamera: boolean): void {
        throw new Error("Remote Avatar does not support setIsFrontCamera");
    }
    public enableCamera(enabled: boolean): void {
        this._shouldCamera$.setValue(enabled);
    }
    public enableMic(enabled: boolean): void {
        this._shouldMic$.setValue(enabled);
    }
    public setElement(element: HTMLElement | null): void {
        this._el$.setValue(element);
    }
    public setActive(active: boolean): void {
        this._active$.setValue(active);
    }
    public updateElementView(element: HTMLElement | null): void {
        const handleUpdateView = async (): Promise<void> => {
            this._el$.setValue(element);
            if (this._shouldCamera$.value) {
                try {
                    await this._rtc.rtc.updateRemoteVideo({
                        view: element,
                        userId: this.uid,
                        // @ts-ignore
                        streamType: TRTCStreamType.Main,
                        option: {
                            mirror: false,
                            fillMode: "cover",
                            receiveWhenViewVisible: true,
                        },
                    });
                } catch (error) {
                    if (error.message.includes("not started")) {
                        this._rtc.rtc.startRemoteVideo({
                            view: element,
                            userId: this.uid,
                            // @ts-ignore
                            streamType: TRTCStreamType.Main,
                            option: {
                                mirror: false,
                                fillMode: "cover",
                                receiveWhenViewVisible: true,
                            },
                        });
                    }
                }
            }
        };
        handleUpdateView();
    }
    public getVolumeLevel(): number {
        return this._rtc.getVolumeLevel() || 0;
    }
    public destroy(): void {
        this._sideEffect.flushAll();
    }
    public enableRemoteAudio(enabled: boolean): void {
        this._shouldMic$.setValue(enabled);
    }
    public getVideoTrack(processed?: boolean): MediaStreamTrack | null {
        return this._rtc.rtc.getVideoTrack({
            userId: this.uid,
            // @ts-ignore
            streamType: TRTCStreamType.Main,
            processed,
        });
    }
    public getAudioTrack(processed?: boolean): MediaStreamTrack | null {
        return this._rtc.rtc.getAudioTrack({
            userId: this.uid,
            // @ts-ignore
            streamType: TRTCStreamType.Main,
            processed,
        });
    }

    public refreshAvatar(): void {
        const el = this._el$.value;
        const active = this._active$.value;
        const shouldCamera = this._shouldCamera$.value;
        const shouldMic = this._shouldMic$.value;
        console.log(
            "[TRTC] [WEB] [LOG] remoteAvatar refreshAvatar是否播放本地流",
            el,
            shouldCamera,
            active,
        );
        if (el && active) {
            if (shouldCamera) {
                this._rtc.rtc.startRemoteVideo({
                    view: el,
                    userId: this.uid,
                    // @ts-ignore
                    streamType: TRTCStreamType.Main,
                    option: {
                        mirror: false,
                        fillMode: "cover",
                        receiveWhenViewVisible: true,
                    },
                });
            } else {
                this._rtc.rtc.stopRemoteVideo({
                    userId: this.uid,
                    // @ts-ignore
                    streamType: TRTCStreamType.Main,
                });
            }
        }
        this._rtc.rtc.muteRemoteAudio(this.uid, !(el && active && shouldMic));
    }

    public hasEl(): boolean {
        return !!this._el$.value;
    }
}
